import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import WizardModal from "@/components/resume/WizardModal";
import { STATUS_KEYS, CHANNEL_KEYS, JOB_TYPE_KEYS, statusLabelKey, channelLabelKey, jobTypeLabelKey } from "@/lib/constants";
import { ArrowLeft, ArrowRight, Check, Mail, Sparkles, PenLine, ClipboardPaste, Loader2, Compass } from "lucide-react";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);

/**
 * Small "Plakken" affordance for users who don't know Ctrl/⌘+V or the
 * right-click paste. Reads the clipboard, hands the text to `onPaste`.
 * Renders as a compact icon-button that sits absolutely in the top-right
 * of the wrapped input so the layout stays tidy.
 */
function PasteButton({ onPaste, testId, title, multiline = false, t }) {
  const handle = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (!text || !text.trim()) {
        toast.error(t("addPasteEmpty"));
        return;
      }
      onPaste(multiline ? text : text.trim());
      toast.success(t("addPasted"));
    } catch {
      toast.error(t("addPasteDenied"));
    }
  };
  return (
    <button
      type="button"
      data-testid={testId}
      title={title}
      onClick={handle}
      tabIndex={-1}
      className="absolute top-6 right-0 p-1.5 text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
    >
      <ClipboardPaste size={13} strokeWidth={1.75} />
    </button>
  );
}

export default function NewJobWizard({ onClose, onCreated, onLetter, onPrep, initialUrl = "", onGoToJobSearch }) {
  const { t } = useT();
  const [step, setStep] = useState(1);
  const [scraping, setScraping] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [job, setJob] = useState(null); // created job (step 3)
  const [form, setForm] = useState({
    url: initialUrl || "", job_title: "", company: "", description: "",
    status: "applied", date_applied: today(), channel: "linkedin",
    recruiter_name: "", job_type: "incompany", location: "", hours_per_week: "",
  });
  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  // Auto-scrape when the wizard opens with a pre-filled URL — powered by
  // the global dashboard-level paste handler that pipes copied job links
  // straight into this wizard.
  useEffect(() => {
    if (initialUrl && /^https?:\/\/\S+\.\S+/.test(initialUrl.trim())) {
      scrape(initialUrl.trim());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialUrl]);

  const scrape = async (overrideUrl) => {
    const url = (overrideUrl ?? form.url).trim();
    if (!url) { toast.error(t("addUrlFirst")); return; }
    setScraping(true);
    try {
      const { data } = await api.post("/scrape", { url });
      if (data.success) {
        setForm((f) => ({
          ...f,
          job_title: data.title || f.job_title,
          company: data.company || f.company,
          description: data.description || f.description,
        }));
        toast.success(t("addScrapeOk"));
        setStep(2);
      } else {
        toast.error(data.error || t("ltwScrapeFailed"));
        setStep(2);
      }
    } catch {
      toast.error(t("ltwScrapeFailed"));
      setStep(2);
    } finally {
      setScraping(false);
    }
  };

  const parseText = async () => {
    const raw = (pasteText || "").trim();
    if (raw.length < 60) return;
    setParsing(true);
    try {
      const { data } = await api.post("/scrape/parse-text", { text: raw, lang: "nl" });
      if (data.success) {
        setForm((f) => ({
          ...f,
          job_title: data.title || f.job_title,
          company: data.company || f.company,
          description: data.description || f.description,
        }));
        toast.success(t("addParseTextOk"));
        setStep(2);
      } else {
        toast.error(data.error || t("addParseTextFail"));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("addParseTextFail"));
    } finally {
      setParsing(false);
    }
  };

  const save = async () => {
    // Company mag leeg blijven — open sollicitaties/referrals bereiken de
    // gebruiker soms zonder bedrijfsnaam. Alleen job_title is verplicht.
    if (!form.job_title.trim()) { toast.error(t("addRequired")); return; }
    if (form.channel === "recruiter" && !form.recruiter_name.trim()) { toast.error(t("addRecruiterRequired")); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        job_title: form.job_title.trim(),
        company: form.company.trim(),
        hours_per_week: Number.isInteger(parseInt(form.hours_per_week, 10)) ? parseInt(form.hours_per_week, 10) : null,
      };
      const { data } = await api.post("/jobs", payload);
      setJob(data);
      onCreated?.();
      toast.success(t("njwSaved"));
      setStep(3);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("addSaveFailed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <WizardModal testId="newjob-wizard" title={t("njwTitle")} onClose={onClose} wide>
      <p className="font-serif text-xl italic text-neutral-800 mb-5">
        {step === 1 ? t("njwStep1") : step === 2 ? t("njwStep2") : t("njwStep3")}
      </p>

      {step === 1 && (
        <div className="space-y-5">
          <div>
            <label className="ed-label">{t("addUrl")}</label>
            <div className="flex gap-2">
              <input
                data-testid="newjob-url"
                className="ed-input"
                placeholder="https://…"
                value={form.url}
                onChange={(e) => set("url", e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); scrape(); } }}
                onPaste={(e) => {
                  // Auto-scrape when the user pastes a URL directly into the
                  // field via Ctrl/⌘+V or right-click → paste. Uses the raw
                  // clipboard data to avoid racing with the async input
                  // onChange event.
                  const text = e.clipboardData?.getData("text") || "";
                  const clean = text.trim();
                  if (clean && /^https?:\/\/\S+\.\S+/.test(clean)) {
                    // Let the browser fill the input first, then kick off
                    // the scrape on the next tick — with the pasted URL
                    // handed in explicitly so we don't wait for React state.
                    setTimeout(() => scrape(clean), 0);
                  }
                }}
              />
              <button
                data-testid="newjob-paste"
                type="button"
                title={t("addPasteHint")}
                onClick={async () => {
                  try {
                    const text = await navigator.clipboard.readText();
                    if (!text || !text.trim()) {
                      toast.error(t("addPasteEmpty"));
                      return;
                    }
                    const clean = text.trim();
                    set("url", clean);
                    toast.success(t("addPasted"));
                    // Auto-scrape when the clipboard content looks like a
                    // valid http(s) URL — saves the user a second click.
                    if (/^https?:\/\/\S+\.\S+/.test(clean)) {
                      scrape(clean);
                    }
                  } catch {
                    toast.error(t("addPasteDenied"));
                  }
                }}
                className="font-mono text-[0.65rem] uppercase tracking-[0.15em] border border-neutral-300 bg-white text-neutral-700 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors px-3 py-2 inline-flex items-center gap-1.5 shrink-0"
              >
                <ClipboardPaste size={12} strokeWidth={1.75} /> {t("addPaste")}
              </button>
              <button data-testid="newjob-scrape" className="ed-btn shrink-0" onClick={scrape} disabled={scraping}>
                {scraping ? t("addScraping") : t("addScrape")} <ArrowRight size={14} strokeWidth={1.5} />
              </button>
            </div>
            <p className="text-xs text-neutral-500 mt-3 leading-relaxed">{t("addScrapeHelp")}</p>
          </div>
          <button data-testid="newjob-manual" onClick={() => setStep(2)} className="font-mono text-xs uppercase tracking-[0.15em] border-b border-neutral-400 pb-0.5 hover:border-neutral-900 transition-colors inline-flex items-center gap-2">
            <PenLine size={12} strokeWidth={1.5} /> {t("njwManual")}
          </button>

          {onGoToJobSearch && (
            <div className="pt-4 border-t border-neutral-200">
              <button
                data-testid="newjob-via-crawler"
                type="button"
                onClick={() => { onClose?.(); onGoToJobSearch(); }}
                className="w-full flex items-center gap-4 border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-50 transition-colors px-4 py-4 text-left"
              >
                <div className="w-10 h-10 shrink-0 rounded-full bg-neutral-900 text-[#F9F8F6] flex items-center justify-center">
                  <Compass size={16} strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-serif text-base leading-tight">{t("njwViaCrawler")}</p>
                  <p className="text-xs text-neutral-600 mt-1 leading-relaxed">{t("njwViaCrawlerHint")}</p>
                </div>
                <ArrowRight size={14} strokeWidth={1.5} className="shrink-0 text-neutral-500" />
              </button>
            </div>
          )}

          {/* AI text splitter — paste the full vacancy text and let AI
              dispatch it into the right fields. Ideal for pastes from
              emails, PDFs, chat messages where no URL is available. */}
          <div className="pt-4 border-t border-neutral-200">
            <label className="ed-label">{t("addPasteText")}</label>
            <div className="relative">
              <textarea
                data-testid="newjob-paste-text"
                className="ed-input pr-8 min-h-[7rem]"
                placeholder={t("addPasteTextPh")}
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
              />
              <PasteButton t={t} testId="newjob-paste-text-btn" title={t("addPasteHint")} onPaste={(v) => setPasteText(v)} multiline />
            </div>
            <button
              data-testid="newjob-parse-text"
              type="button"
              disabled={pasteText.trim().length < 60 || parsing}
              onClick={parseText}
              className="ed-btn mt-3"
            >
              {parsing ? <><Loader2 size={13} strokeWidth={1.75} className="animate-spin" /> {t("addParseTextBusy")}</> : <><Sparkles size={13} strokeWidth={1.75} /> {t("addParseTextCta")}</>}
            </button>
            <p className="text-xs text-neutral-500 mt-2 leading-relaxed">{t("addPasteTextHelp")}</p>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="relative">
              <label className="ed-label">{t("addPosition")}</label>
              <input data-testid="newjob-title" className="ed-input pr-8" value={form.job_title} onChange={(e) => set("job_title", e.target.value)} />
              <PasteButton t={t} testId="newjob-title-paste" title={t("addPasteHint")} onPaste={(v) => set("job_title", v)} />
            </div>
            <div className="relative">
              <label className="ed-label">{t("addCompany")} <span className="text-neutral-400 font-normal normal-case">({t("optionalLabel")})</span></label>
              <input data-testid="newjob-company" className="ed-input pr-8" value={form.company} onChange={(e) => set("company", e.target.value)} placeholder={t("addCompanyPlaceholder")} />
              <PasteButton t={t} testId="newjob-company-paste" title={t("addPasteHint")} onPaste={(v) => set("company", v)} />
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <label className="ed-label">{t("addStatus")}</label>
              <select data-testid="newjob-status" className="ed-input" value={form.status} onChange={(e) => set("status", e.target.value)}>
                {STATUS_KEYS.map((k) => <option key={k} value={k}>{t(statusLabelKey(k))}</option>)}
              </select>
            </div>
            <div>
              <label className="ed-label">{t("addDate")}</label>
              <input data-testid="newjob-date" type="date" className="ed-input" value={form.date_applied} onChange={(e) => set("date_applied", e.target.value)} />
            </div>
            <div>
              <label className="ed-label">{t("addChannel")}</label>
              <select data-testid="newjob-channel" className="ed-input" value={form.channel} onChange={(e) => set("channel", e.target.value)}>
                {CHANNEL_KEYS.map((k) => <option key={k} value={k}>{t(channelLabelKey(k))}</option>)}
              </select>
            </div>
            <div>
              <label className="ed-label">{t("addJobType")}</label>
              <select data-testid="newjob-type" className="ed-input" value={form.job_type} onChange={(e) => set("job_type", e.target.value)}>
                {JOB_TYPE_KEYS.map((k) => <option key={k} value={k}>{t(jobTypeLabelKey(k))}</option>)}
              </select>
            </div>
          </div>
          {form.channel === "recruiter" && (
            <div>
              <label className="ed-label">{t("addRecruiterName")}</label>
              <input data-testid="newjob-recruiter" className="ed-input max-w-sm" value={form.recruiter_name} onChange={(e) => set("recruiter_name", e.target.value)} />
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="relative">
              <label className="ed-label">{t("addLocation")}</label>
              <input data-testid="newjob-location" className="ed-input pr-8" value={form.location} onChange={(e) => set("location", e.target.value)} />
              <PasteButton t={t} testId="newjob-location-paste" title={t("addPasteHint")} onPaste={(v) => set("location", v)} />
            </div>
            <div>
              <label className="ed-label">{t("addHoursPerWeek")}</label>
              <input data-testid="newjob-hours" type="number" min="1" max="80" placeholder={t("addHoursPerWeekPh")} className="ed-input" value={form.hours_per_week} onChange={(e) => set("hours_per_week", e.target.value)} />
            </div>
          </div>
          <div className="relative">
            <label className="ed-label">{t("addDescription")}</label>
            <textarea data-testid="newjob-description" className="ed-input pr-8 min-h-[8rem]" placeholder={t("addDescriptionPh")} value={form.description} onChange={(e) => set("description", e.target.value)} />
            <PasteButton t={t} testId="newjob-description-paste" title={t("addPasteHint")} onPaste={(v) => set("description", v)} multiline />
          </div>
          <div className="flex items-center justify-between pt-2">
            <button data-testid="newjob-back" className="ed-btn ed-btn-outline" onClick={() => setStep(1)}>
              <ArrowLeft size={14} strokeWidth={1.5} /> {t("wizBack")}
            </button>
            <button data-testid="newjob-save" className="ed-btn" onClick={save} disabled={saving}>
              <Check size={14} strokeWidth={1.5} /> {saving ? t("saving") : t("njwSave")}
            </button>
          </div>
        </div>
      )}

      {step === 3 && job && (
        <div className="space-y-6">
          <div className="ed-card p-6">
            <p className="font-serif text-2xl leading-tight">{job.job_title}</p>
            <p className="font-serif text-lg italic text-neutral-600 mt-1">{job.company}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <button data-testid="newjob-act-letter" className="ed-btn justify-center" onClick={() => onLetter(job)}>
              <Mail size={14} strokeWidth={1.5} /> {t("njwActLetter")}
            </button>
            <button data-testid="newjob-act-prep" className="ed-btn ed-btn-outline justify-center" onClick={() => onPrep(job)}>
              <Sparkles size={14} strokeWidth={1.5} /> {t("njwActPrep")}
            </button>
            <button data-testid="newjob-act-done" className="ed-btn ed-btn-outline justify-center" onClick={onClose}>
              {t("njwActDone")}
            </button>
          </div>
        </div>
      )}
    </WizardModal>
  );
}
